import { useEffect, useState, useCallback } from "react";
import { Copy, Check } from "lucide-react";
import styles from "./ChatView.module.css";

interface Props {
  code: string;
  lang: string;
}

let highlighter: ((code: string, lang: string) => string) | null = null;
let highlighterLoading: Promise<void> | null = null;

function loadHighlighter(): Promise<void> {
  if (highlighter) return Promise.resolve();
  if (highlighterLoading) return highlighterLoading;
  highlighterLoading = import("../lib/highlightCore").then((m) => {
    highlighter = m.highlightCode;
  });
  return highlighterLoading;
}

export function CodeBlock({ code, lang }: Props) {
  const [html, setHtml] = useState<string | null>(highlighter ? highlighter(code, lang) : null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (html != null) return;
    let alive = true;
    loadHighlighter().then(() => {
      if (alive && highlighter) setHtml(highlighter(code, lang));
    });
    return () => {
      alive = false;
    };
  }, [code, lang, html]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code.trim()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [code]);

  return (
    <div className={styles.codeBlockWrapper}>
      <div className={styles.codeBlockHeader}>
        <span>{lang || "code"}</span>
        <button
          type="button"
          onClick={handleCopy}
          className={`${styles.copyCodeBtn} ${copied ? styles.copyCodeBtnDone : ""}`}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </div>
      <pre className={styles.codeBlock}>
        {html != null ? (
          <code dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <code>{code}</code>
        )}
      </pre>
    </div>
  );
}
